import { Button, type TabItem, TabPanel, TabView } from '@ics-portal/guardian';
import { clsx } from 'clsx';
import {
    type ForwardedRef,
    forwardRef,
    type ReactNode,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import s from './TabbedInfoCard.module.scss';

export interface TabbedInfoCardApi {
    setActiveTab: (tabName: string) => void;
}

interface TabbedInfoCardProps {
    selectedKey?: string;
    onSelectionChange?: (selectedKey: string) => void;
    tabItems: TabItem[];
}

function TabbedInfoCardComponent(
    { selectedKey, onSelectionChange, tabItems }: TabbedInfoCardProps,
    forwardedRef: ForwardedRef<TabbedInfoCardApi>,
): ReactNode {
    const initialSelectedKeyApplied = useRef(false);

    const getInitialIndex = (): number => {
        if (!selectedKey) return 0;
        const index = tabItems.findIndex((tab) => tab.name === selectedKey);
        return index !== -1 ? index : 0;
    };

    const [activeIndex, setActiveIndex] = useState(getInitialIndex);

    useImperativeHandle(forwardedRef, () => ({
        setActiveTab: (tabName: string) => {
            const newIndex = tabItems.findIndex((tab) => tab.name === tabName);
            if (newIndex !== -1) {
                setActiveIndex(newIndex);
            }
        },
    }));

    useEffect(() => {
        if (!selectedKey || initialSelectedKeyApplied.current) {
            return;
        }

        const newIndex = tabItems.findIndex((tab) => tab.name === selectedKey);
        if (newIndex !== -1 && newIndex !== activeIndex) {
            setActiveIndex(newIndex);
        }
        initialSelectedKeyApplied.current = true;
    }, [activeIndex, selectedKey, tabItems]);

    function handleTabChange(newIndex: number): void {
        setActiveIndex(newIndex);
        if (onSelectionChange) {
            const newTab = tabItems[newIndex];
            onSelectionChange(newTab.name);
        }
    }

    return (
        <TabView
            className={clsx(s.tabView, 'h-full bg-n20 flex flex-column')}
            activeIndex={activeIndex}
            onTabChange={(e) => {
                handleTabChange(e.index);
            }}
        >
            {tabItems.map((tabItem, index) => (
                <TabPanel
                    className={s.tabPanel}
                    key={tabItem.name}
                    headerTemplate={
                        <Button
                            className={clsx(s.tabButton, {
                                [s.active]: index === activeIndex,
                            })}
                            icon={tabItem.icon}
                            tooltip={tabItem.tooltip}
                            data-pr-position="bottom"
                            onClick={() => {
                                handleTabChange(index);
                            }}
                        />
                    }
                >
                    {tabItem.content}
                </TabPanel>
            ))}
        </TabView>
    );
}

export const TabbedInfoCard = forwardRef(TabbedInfoCardComponent);
export default TabbedInfoCard;

export { default as InfoCardWrapper } from './Layout/InfoCardWrapper';
